// src/components/events/EventSettingsModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, User, Save, Settings, Calendar, MapPin, Users, CreditCard, MessageSquare,
  Eye, Lock, Globe, Palette, Heart, Gift, CheckCircle, UserPlus, Trash2, Layout,
  Camera, Truck, Upload, ExternalLink, Type, Grid, Building2, Phone, Mail,
  Clock, Star, Bell, Shield
} from 'lucide-react';
import { firestoreService } from '../../services/firestoreService';
import { FileUpload } from '../common/FileUpload';

import { OrganizerInvitation } from './OrganizerInvitation';
import { invitationsService } from '../../services/invitationsService';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
// Event Categories with presets (like Album categories)
const EVENT_CATEGORIES = {
  'social': {
    label: 'Social Event',
    icon: Users,
    color: '#10b981',
    description: 'Birthday parties, casual gatherings, celebrations',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      dress_code: true,
      food_menu: true,
      entertainment: true,
      parking: true
    }
  },
  'wedding': {
    label: 'Wedding',
    icon: Heart,
    color: '#f472b6',
    description: 'Wedding ceremonies, receptions, and celebrations',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      ceremony_time: true,
      reception_time: true,
      dress_code: true,
      accommodation: true,
      gift_registry: true,
      transportation: true,
      meal_preference: true
    }
  },
  'reunion': {
    label: 'Reunion',
    icon: Users,
    color: '#8b5cf6',
    description: 'School, family, or group reunions',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      year_graduated: true,
      accommodation: true,
      activities: true,
      photo_sharing: true,
      contact_updates: true
    }
  },
  'seminar': {
    label: 'Seminar/Workshop',
    icon: CheckCircle,
    color: '#3b82f6',
    description: 'Educational seminars, workshops, training sessions',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      agenda: true,
      speakers: true,
      materials: true,
      certificates: true,
      prerequisites: true,
      lunch_provided: true
    }
  },
  'conference': {
    label: 'Conference',
    icon: Building2,
    color: '#059669',
    description: 'Professional conferences, conventions, trade shows',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      agenda: true,
      speakers: true,
      networking: true,
      accommodation: true,
      sponsor_info: true,
      exhibition: true,
      lunch_provided: true
    }
  },
  'fundraiser': {
    label: 'Fundraiser',
    icon: Gift,
    color: '#dc2626',
    description: 'Charity events, fundraising campaigns',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      cause_description: true,
      donation_tiers: true,
      sponsors: true,
      auction_items: true,
      volunteer_info: true
    }
  },
  'corporate': {
    label: 'Corporate Event',
    icon: Building2,
    color: '#6366f1',
    description: 'Business meetings, company parties, team building',
    defaultFields: {
      eventDate: true,
      eventLocation: true,
      agenda: true,
      department: true,
      dress_code: true,
      parking: true,
      catering: true
    }
  }
};

// Event-specific booking fields (like media card fields in Album)
const EVENT_BOOKING_FIELDS = {
  // Personal Info
  name: { label: 'Full Name', type: 'text', icon: Users, required: true },
  email: { label: 'Email Address', type: 'email', icon: Mail, required: true },
  phone: { label: 'Phone Number', type: 'tel', icon: Phone },
  company: { label: 'Company/Organization', type: 'text', icon: Building2 },
  
  // Event Specific
  dietary_restrictions: { label: 'Dietary Restrictions', type: 'text', icon: Users },
  accessibility_needs: { label: 'Accessibility Needs', type: 'text', icon: Users },
  meal_preference: { label: 'Meal Preference', type: 'select', icon: Users },
  accommodation_needed: { label: 'Accommodation Needed', type: 'checkbox', icon: Building2 },
  transportation_needed: { label: 'Transportation Needed', type: 'checkbox', icon: MapPin },
  
  // Wedding Specific
  guest_type: { label: 'Guest Type', type: 'select', icon: Heart },
  song_requests: { label: 'Song Requests', type: 'textarea', icon: Users },
  plus_one: { label: 'Plus One', type: 'text', icon: Users },
  
  // Reunion Specific  
  year_graduated: { label: 'Year Graduated', type: 'number', icon: Calendar },
  maiden_name: { label: 'Maiden Name', type: 'text', icon: Users },
  current_city: { label: 'Current City', type: 'text', icon: MapPin },
  
  // Professional Events
  job_title: { label: 'Job Title', type: 'text', icon: Building2 },
  industry: { label: 'Industry', type: 'text', icon: Building2 },
  linkedin: { label: 'LinkedIn Profile', type: 'url', icon: Building2 },
  session_preference: { label: 'Session Preference', type: 'select', icon: CheckCircle },
  
  // Special Requests
  special_requests: { label: 'Special Requests', type: 'textarea', icon: Users }
};

export const EventSettingsModal = ({ book, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    // Event Category
    eventCategory: 'social',
    
    // Booking Fields Configuration  
    enabledFields: {},
    fieldSettings: {},
    
    // Basic Info
    botName: '',
    description: '',
    eventDate: '',
    eventLocation: '',
    venue: '',
    venueDetails: '',
    maxAttendees: 100,
    duration: '',
    coverImageUrl: '',
    logoUrl: '',
    heroImage: '',
    
    // Custom Styling
    primaryColor: '#10b981',
    backgroundColor: '#ffffff',
    headerColor: '#ffffff',
    headerTextColor: '#333333',
    accentColor: '#f59e0b',
    fontFamily: 'Inter, sans-serif',
    headerRadius: '0px',
    cardRadius: '12px',
    shadowLevel: 'medium',
    
    // Booking Settings
    requireBooking: true,
    publicBooking: true,
    requirePayment: false,
    eventPrice: 0,
    bookingDeadline: '',
    autoConfirm: true,
    allowWaitlist: false,
    confirmationEmail: true,
    reminderEmails: true,
    
    // Features & Interactions
    giftRegistryEnabled: false,
    giftRegistryPublic: false,
    milestoneTracking: false,
    publicMilestones: false,
    supplierManagement: false,
    checkInSystem: false,
    galleryManagement: true,
    publicGallery: true,
    allowComments: true,
    allowSharing: true,
    showAttendeeCount: true,
    enableQRCode: true,
    
    // Privacy & Access
    accessLevel: 'public',
    allowAnonymous: true,
    requireLoginForBooking: false,
    moderateComments: false,
    published: false,
    
    // SEO & Domain
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    ogImage: '',
    favicon: '',
    enableStructuredData: true,
    allowIndexing: true,
    
    // Contact & Organization
    organizers: [
      {
        id: 'org_1',
        name: '',
        email: '',
        phone: '',
        avatar: '',
        role: 'Primary Organizer',
        isMain: true
      }
    ],

    enableMessages: false,
    enableUpdates: true,
    moderateMessages: true,
    moderateUpdates: false,
    allowAnonymousMessages: false,
    messagingRoles: ['organizer', 'booked_attendee'],
    updatesRoles: ['organizer'],
    
    // Location & Logistics
    fullAddress: '',
    mapLink: '',
    parkingInfo: '',
    publicTransport: '',
    accessibilityInfo: '',
    weatherBackup: '',
    
    // Advanced Settings
    enableAnalytics: true,
    customCSS: '',
    enableMultiLanguage: false,
    timezone: 'America/New_York',
    emailNotifications: true,
    smsNotifications: false
  });

  const [activeTab, setActiveTab] = useState('category');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStates, setUploadStates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // File states
  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);

  const [previewMode, setPreviewMode] = useState('desktop');

  const [validationErrors, setValidationErrors] = useState({});

  // Initialize form data
  useEffect(() => {
    if (book) {
      const category = book.eventSettings?.eventCategory || 'social';
      const categoryPreset = EVENT_CATEGORIES[category];
      
      setFormData({
        // Event Category
        eventCategory: category,
        
        // Booking Fields Configuration
        enabledFields: book.eventSettings?.enabledFields || categoryPreset?.defaultFields || {},
        fieldSettings: book.eventSettings?.fieldSettings || {},
        
        // Basic Info
        botName: book.botName || '',
        description: book.description || '',
        eventDate: book.eventSettings?.eventDate || '',
        eventLocation: book.eventSettings?.eventLocation || '',
        venue: book.eventSettings?.venue || '',
        venueDetails: book.eventSettings?.venueDetails || '',
        maxAttendees: book.eventSettings?.maxAttendees || 100,
        duration: book.eventSettings?.duration || '',
        coverImageUrl: book.coverImageUrl || '',
        logoUrl: book.logoUrl || '',
        heroImage: book.heroImage || '',

        headerSettings: {
          // Element Visibility
          showLogo: book.headerSettings?.showLogo ?? true,
          showTitle: book.headerSettings?.showTitle ?? true,
          showDescription: book.headerSettings?.showDescription ?? false,
          showSearch: book.headerSettings?.showSearch ?? true,
          showShareButton: book.headerSettings?.showShareButton ?? true,
          showAccessLevel: book.headerSettings?.showAccessLevel ?? true,
          showFilterControls: book.headerSettings?.showFilterControls ?? true,
          showGallerySelector: book.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: book.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: book.headerSettings?.showOwnerControls ?? true,

          // Styling
          headerHeight: book.headerSettings?.headerHeight || 80,
          logoHeight: book.headerSettings?.logoHeight || 40,
          position: book.headerSettings?.position || 'sticky',
          layout: book.headerSettings?.layout || 'default',

          // Search Configuration
          searchPlaceholder: book.headerSettings?.searchPlaceholder || 'Search media...',
          searchPosition: book.headerSettings?.searchPosition || 'center',
          enableLiveSearch: book.headerSettings?.enableLiveSearch ?? true,
          showSearchOnMobile: book.headerSettings?.showSearchOnMobile ?? false,

          // User Profile
          userProfileVisibility: book.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: book.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: book.headerSettings?.requireLoginToView ?? false,

          ...book.headerSettings
        },
        
        // Custom Styling
        primaryColor: book.styling?.primaryColor || '#10b981',
        backgroundColor: book.styling?.backgroundColor || '#ffffff',
        headerColor: book.styling?.headerColor || '#ffffff',
        headerTextColor: book.styling?.headerTextColor || '#333333',
        headerOpacity: book.styling?.headerOpacity ?? 100,
        accentColor: book.styling?.accentColor || '#f59e0b',
        fontFamily: book.styling?.fontFamily || 'Inter, sans-serif',
        headerRadius: book.styling?.headerRadius || '0px',
        cardRadius: book.styling?.cardRadius || '12px',
        shadowLevel: book.styling?.shadowLevel || 'medium',
        
        // Booking Settings
        requireBooking: book.eventSettings?.requireBooking !== false,
        publicBooking: book.eventSettings?.publicBooking !== false,
        requirePayment: book.eventSettings?.requirePayment || false,
        eventPrice: book.eventSettings?.eventPrice || 0,
        bookingDeadline: book.eventSettings?.bookingDeadline || '',
        autoConfirm: book.eventSettings?.autoConfirm !== false,
        allowWaitlist: book.eventSettings?.allowWaitlist || false,
        confirmationEmail: book.eventSettings?.confirmationEmail !== false,
        reminderEmails: book.eventSettings?.reminderEmails !== false,
        
        // Features
        giftRegistryEnabled: book.eventSettings?.giftRegistryEnabled || false,
        giftRegistryPublic: book.eventSettings?.giftRegistryPublic || false,
        milestoneTracking: book.eventSettings?.milestoneTracking || false,
        publicMilestones: book.eventSettings?.publicMilestones || false,
        supplierManagement: book.eventSettings?.supplierManagement || false,
        checkInSystem: book.eventSettings?.checkInSystem || false,
        galleryManagement: book.eventSettings?.galleryManagement !== false,
        publicGallery: book.eventSettings?.publicGallery !== false,
        allowComments: book.allowComments !== false,
        allowSharing: book.allowSharing !== false,
        showAttendeeCount: book.eventSettings?.showAttendeeCount !== false,
        enableQRCode: book.eventSettings?.enableQRCode !== false,
        
        // Privacy & Access
        accessLevel: book.accessLevel || 'public',
        allowAnonymous: book.allowAnonymous !== false,
        requireLoginForBooking: book.eventSettings?.requireLoginForBooking || false,
        moderateComments: book.moderateComments || false,
        published: book.published || false,
        
        // SEO & Domain
        customDomain: book.customDomain || '',
        customPath: book.customPath || '',
        seoTitle: book.seoTitle || book.botName || '',
        seoDescription: book.seoDescription || book.description || '',
        seoKeywords: book.seoKeywords || '',
        ogImage: book.ogImage || book.heroImage || '',
        favicon: book.favicon || '',
        enableStructuredData: book.enableStructuredData !== false,
        allowIndexing: book.allowIndexing !== false,
        
        // Contact & Organization
        organizers: book.organizers?.length > 0 ? book.organizers : [
            {
              id: 'org_1',
              name: book.contactInfo?.name || '',
              email: book.contactInfo?.email || '',
              phone: book.contactInfo?.phone || '',
              website: book.contactInfo?.website || '',
              avatar: book.contactInfo?.logo || '',
              role: 'Primary Organizer',
              isMain: true
            }
          ],
        
        // Messages & Updates
        enableMessages: book.eventSettings?.enableMessages || false,
        enableUpdates: book.eventSettings?.enableUpdates !== false,
        moderateMessages: book.eventSettings?.moderateMessages !== false,
        moderateUpdates: book.eventSettings?.moderateUpdates || false,
        allowAnonymousMessages: book.eventSettings?.allowAnonymousMessages || false,
        messagingRoles: book.eventSettings?.messagingRoles || ['organizer', 'booked_attendee'],
        updatesRoles: book.eventSettings?.updatesRoles || ['organizer'],
        
        // Location & Logistics
        fullAddress: book.eventSettings?.fullAddress || '',
        mapLink: book.eventSettings?.mapLink || '',
        parkingInfo: book.eventSettings?.parkingInfo || '',
        publicTransport: book.eventSettings?.publicTransport || '',
        accessibilityInfo: book.eventSettings?.accessibilityInfo || '',
        weatherBackup: book.eventSettings?.weatherBackup || '',
        
        // Advanced Settings
        enableAnalytics: book.enableAnalytics !== false,
        customCSS: book.customCSS || '',
        enableMultiLanguage: book.eventSettings?.enableMultiLanguage || false,
        timezone: book.eventSettings?.timezone || 'America/New_York',
        emailNotifications: book.eventSettings?.emailNotifications !== false,
        smsNotifications: book.eventSettings?.smsNotifications || false
      });
    }
  }, [book]);

  const handleCategoryChange = (category) => {
    const categoryPreset = EVENT_CATEGORIES[category];
    setFormData(prev => ({
      ...prev,
      eventCategory: category,
      enabledFields: {
        ...prev.enabledFields,
        ...categoryPreset.defaultFields
      },
      primaryColor: categoryPreset.color
    }));
  };

  const handleInputChange = (path, value) => {
    console.log('Input change:', path, value); // Debug log
    
    setFormData(prev => {
      // Handle nested paths with dot notation
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        
        // Navigate to parent object
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          
          // Ensure nested object exists
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        
        return newData;
      }
      
      // Handle simple top-level properties
      return {
        ...prev,
        [path]: value
      };
    });
    
    // THIS IS THE KEY LINE THAT WAS MISSING
    setHasChanges(true);
  };

  const addOrganizer = () => {
    const newOrganizer = {
      id: `org_${Date.now()}`,
      name: '',
      email: '',
      phone: '',
      website: '',
      avatar: '',
      role: 'Co-organizer',
      isMain: false
    };
    
    setFormData(prev => ({
      ...prev,
      organizers: [...prev.organizers, newOrganizer]
    }));
  };

  const updateOrganizer = (organizerId, field, value) => {
    setFormData(prev => ({
      ...prev,
      organizers: prev.organizers.map(org => 
        org.id === organizerId ? { ...org, [field]: value } : org
      )
    }));
  };

  const removeOrganizer = (organizerId) => {
    setFormData(prev => ({
      ...prev,
      organizers: prev.organizers.filter(org => org.id !== organizerId)
    }));
  };

  const uploadOrganizerAvatar = async (organizerId, file) => {
    if (!file) return;

    const uploadKey = `org_${organizerId}`;
    setUploadStates(prev => ({ 
      ...prev, 
      [uploadKey]: { uploading: true, error: null } 
    }));

    try {
      // Use Firebase Storage to upload avatar
      const result = await firestoreService.uploadEventFile(file, book.id, `organizer_${organizerId}`);
      
      updateOrganizer(organizerId, 'avatar', result.url);
      
      setUploadStates(prev => ({ 
        ...prev, 
        [uploadKey]: { uploading: false, error: null } 
      }));
    } catch (error) {
      console.error('Error uploading organizer avatar:', error);
      setUploadStates(prev => ({ 
        ...prev, 
        [uploadKey]: { uploading: false, error: 'Upload failed' } 
      }));
    }
  };

  const handleFileUpload = async (fileType, file) => {
    if (!file) return;

    setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));

    try {
      let result;
      switch (fileType) {
        case 'logo':
          result = await firestoreService.uploadEventLogo(file, book.id);
          break;
        case 'heroImage':
          result = await firestoreService.uploadEventHeroImage(file, book.id);
          break;
        case 'favicon':
        case 'ogImage':
          result = await firestoreService.uploadEventFile(file, book.id, fileType);
          break;
        default:
          result = await firestoreService.uploadEventFile(file, book.id, fileType);
      }

      setFormData(prev => ({
        ...prev,
        [fileType === 'heroImage' ? 'heroImage' : 
         fileType === 'logo' ? 'logoUrl' : 
         fileType]: result.url
      }));

      setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
      
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setUploadStates(prev => ({ 
        ...prev, 
        [fileType]: { uploading: false, error: error.message } 
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
        // Validate required fields
        const errors = {};
        if (!formData.botName?.trim()) {
        errors.botName = 'Event name is required';
        }
        if (!formData.eventDate) {
        errors.eventDate = 'Event date is required';
        }
        if (!formData.eventLocation?.trim()) {
        errors.eventLocation = 'Event location is required';
        }

        // Custom domain validation
        if (formData.customDomain && !isValidDomain(formData.customDomain)) {
        errors.customDomain = 'Please enter a valid domain (e.g., event.yourdomain.com)';
        }

        // Custom path validation
        if (formData.customPath && !/^[a-zA-Z0-9-]+$/.test(formData.customPath)) {
        errors.customPath = 'Path can only contain letters, numbers, and hyphens';
        }

        // SEO validation
        if (formData.seoTitle && formData.seoTitle.length > 60) {
        errors.seoTitle = 'SEO title should be 60 characters or less';
        }

        if (formData.seoDescription && formData.seoDescription.length > 160) {
        errors.seoDescription = 'SEO description should be 160 characters or less';
        }

        // If there are validation errors, stop and show them
        if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setSaving(false);
        return;
        }

        // Clear any previous validation errors
        setValidationErrors({});

        // Prepare the complete updated book object
        const updatedBook = {
        // Preserve existing book properties
        ...book,
        
        // Basic info updates
        botName: formData.botName?.trim(),
        description: formData.description?.trim(),
        logoUrl: formData.logoUrl,
        coverImageUrl: formData.coverImageUrl,
        heroImage: formData.heroImage,
        
        // Access & Privacy settings
        accessLevel: formData.accessLevel,
        allowAnonymous: formData.allowAnonymous,
        moderateComments: formData.moderateComments,
        published: formData.published,

        headerSettings: formData.headerSettings, 
        
        // SEO & Domain settings
        customDomain: formData.customDomain?.trim(),
        customPath: formData.customPath?.trim(),
        seoTitle: formData.seoTitle?.trim(),
        seoDescription: formData.seoDescription?.trim(),
        seoKeywords: formData.seoKeywords?.trim(),
        ogImage: formData.ogImage,
        favicon: formData.favicon,
        enableStructuredData: formData.enableStructuredData,
        allowIndexing: formData.allowIndexing,
        
        // Interaction settings
        allowComments: formData.allowComments,
        allowSharing: formData.allowSharing,
        
        // Advanced settings
        enableAnalytics: formData.enableAnalytics,
        customCSS: formData.customCSS?.trim(),
        
        // Styling configuration
        styling: {
            primaryColor: formData.primaryColor,
            backgroundColor: formData.backgroundColor,
            headerColor: formData.headerColor,
            headerTextColor: formData.headerTextColor,
            headerOpacity: formData.headerOpacity,
            accentColor: formData.accentColor,
            fontFamily: formData.fontFamily,
            headerRadius: formData.headerRadius,
            cardRadius: formData.cardRadius,
            shadowLevel: formData.shadowLevel
        },

        // Multiple organizers
        organizers: formData.organizers,
        
        // Contact/Organizer information
        contactInfo: {
          name: formData.organizers[0]?.name || '',
          email: formData.organizers[0]?.email || '',
          phone: formData.organizers[0]?.phone || '',
          website: formData.organizers[0]?.website || '',
          logo: formData.organizers[0]?.avatar || ''
        },
        
        // Complete event-specific settings
        eventSettings: {
            // Event category and fields
            eventCategory: formData.eventCategory,
            enabledFields: formData.enabledFields || {},
            fieldSettings: formData.fieldSettings || {},
            
            // Core event details
            eventDate: formData.eventDate,
            eventLocation: formData.eventLocation?.trim(),
            venue: formData.venue?.trim(),
            venueDetails: formData.venueDetails?.trim(),
            maxAttendees: parseInt(formData.maxAttendees) || 100,
            duration: formData.duration?.trim(),
            
            // Booking configuration
            requireBooking: Boolean(formData.requireBooking),
            publicBooking: Boolean(formData.publicBooking),
            requirePayment: Boolean(formData.requirePayment),
            eventPrice: parseFloat(formData.eventPrice) || 0,
            bookingDeadline: formData.bookingDeadline,
            autoConfirm: formData.autoConfirm,
            allowWaitlist: formData.allowWaitlist,
            confirmationEmail: formData.confirmationEmail,
            reminderEmails: formData.reminderEmails,
            requireLoginForBooking: formData.requireLoginForBooking,

            // Messages & Updates settings
            enableMessages: formData.enableMessages,
            enableUpdates: formData.enableUpdates,
            moderateMessages: formData.moderateMessages,
            moderateUpdates: formData.moderateUpdates,
            allowAnonymousMessages: formData.allowAnonymousMessages,
            messagingRoles: formData.messagingRoles,
            updatesRoles: formData.updatesRoles,
            
            // Feature toggles
            giftRegistryEnabled: formData.giftRegistryEnabled,
            giftRegistryPublic: formData.giftRegistryPublic,
            milestoneTracking: formData.milestoneTracking,
            publicMilestones: formData.publicMilestones,
            supplierManagement: formData.supplierManagement,
            checkInSystem: formData.checkInSystem,
            galleryManagement: formData.galleryManagement,
            publicGallery: formData.publicGallery,
            showAttendeeCount: formData.showAttendeeCount,
            enableQRCode: formData.enableQRCode,
            
            // Location & logistics details
            fullAddress: formData.fullAddress?.trim(),
            mapLink: formData.mapLink?.trim(),
            parkingInfo: formData.parkingInfo?.trim(),
            publicTransport: formData.publicTransport?.trim(),
            accessibilityInfo: formData.accessibilityInfo?.trim(),
            weatherBackup: formData.weatherBackup?.trim(),
            
            // Advanced event settings
            enableMultiLanguage: formData.enableMultiLanguage,
            timezone: formData.timezone,
            emailNotifications: formData.emailNotifications,
            smsNotifications: formData.smsNotifications
        },
        
        // Update metadata
        updatedAt: new Date(),
        lastModifiedBy: 'event-settings-modal'
        };
        // Call the parent update function
        await onUpdate(updatedBook);
        
        // Show success message
        console.log('Event settings saved successfully');
        
        // Close the modal
        onClose();
        
    } catch (error) {
        console.error('Error saving event settings:', error);
        
        // Show user-friendly error message
        let errorMessage = 'Failed to save settings. Please try again.';
        
        if (error.message.includes('permission')) {
        errorMessage = 'You do not have permission to update this event.';
        } else if (error.message.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your inputs and try again.';
        }
        
        alert(errorMessage);
    } finally {
        setSaving(false);
    }
    };

    // Helper function for domain validation
    const isValidDomain = (domain) => {
    try {
        // Remove protocol if present
        const cleanDomain = domain.replace(/^https?:\/\//, '');
        
        // Basic domain regex pattern
        const domainPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        
        return domainPattern.test(cleanDomain) && cleanDomain.length <= 253;
    } catch {
        return false;
    }
    };

  if (!isOpen) return null;

  const currentCategory = EVENT_CATEGORIES[formData.eventCategory];
  const previewUrl = formData.customDomain ? 
    `https://${formData.customDomain}` : 
    `https://parrotpages.com/${formData.customPath || 'your-event'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Event Settings
            {currentCategory && (
              <span className="ml-2 px-3 py-1 rounded-full text-sm" style={{ 
                backgroundColor: currentCategory.color + '20', 
                color: currentCategory.color 
              }}>
                {currentCategory.label}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-64 border-r bg-gray-50 p-4 overflow-y-auto">
            <nav className="space-y-2">
              {[
                { id: 'category', label: 'Event Type', icon: Grid },
                { id: 'fields', label: 'Booking Fields', icon: Type },
                { id: 'basic', label: 'Basic Info', icon: Calendar },
                { id: 'header', label: 'Header', icon: Layout },
                { id: 'location', label: 'Location & Logistics', icon: MapPin },
                { id: 'booking', label: 'Booking Settings', icon: CreditCard },
                { id: 'features', label: 'Features', icon: CheckCircle },
                { id: 'styling', label: 'Styling & Branding', icon: Palette },
                { id: 'design', label: 'Brand Design', icon: Palette },
                { id: 'organizers', label: 'Organizers', icon: Users }, // NEW TAB
                { id: 'messages', label: 'Messages & Updates', icon: MessageSquare },
                { id: 'access', label: 'Access & Privacy', icon: Lock },
                { id: 'seo', label: 'Domain & SEO', icon: Globe },
                { id: 'advanced', label: 'Advanced', icon: Settings },
                { id: 'preview', label: 'Preview', icon: Eye }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Event Category Tab */}
            {activeTab === 'category' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Event Type</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Select the type of event you're organizing. This will configure appropriate booking fields and settings.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(EVENT_CATEGORIES).map(([key, category]) => {
                    const IconComponent = category.icon;
                    const isSelected = formData.eventCategory === key;
                    
                    return (
                      <button
                        key={key}
                        onClick={() => handleCategoryChange(key)}
                        className={`p-6 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                          isSelected
                            ? 'border-2 shadow-lg'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        style={{
                          borderColor: isSelected ? category.color : undefined,
                          backgroundColor: isSelected ? category.color + '10' : undefined
                        }}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: category.color + '20' }}
                          >
                            <IconComponent 
                              className="h-5 w-5" 
                              style={{ color: category.color }}
                            />
                          </div>
                          <h4 className="font-semibold text-gray-900">{category.label}</h4>
                        </div>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Booking Fields Tab */}
            {activeTab === 'fields' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Configure Booking Fields</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Choose which information to collect from attendees when they book your event.
                  </p>
                </div>

                {/* Preview of selected category */}
                {currentCategory && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <currentCategory.icon 
                        className="h-5 w-5" 
                        style={{ color: currentCategory.color }} 
                      />
                      <span className="font-medium text-gray-900">
                        {currentCategory.label} Default Fields
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{currentCategory.description}</p>
                  </div>
                )}

                {/* Field Groups */}
                <div className="space-y-8">
                  {/* Required Fields */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Required Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(EVENT_BOOKING_FIELDS)
                        .filter(([_, field]) => field.required)
                        .map(([key, field]) => {
                          const IconComponent = field.icon;
                          
                          return (
                            <div 
                              key={key}
                              className="flex items-start gap-3 p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50"
                            >
                              <div className="flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={true}
                                  disabled
                                  className="mt-1 rounded border-gray-300 text-emerald-600"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComponent className="h-4 w-4 text-emerald-600" />
                                  <span className="font-medium text-gray-900">{field.label}</span>
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Required</span>
                                </div>
                                <p className="text-sm text-gray-600">Always collected for all bookings</p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Optional Fields */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Optional Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(EVENT_BOOKING_FIELDS)
                        .filter(([_, field]) => !field.required)
                        .map(([key, field]) => {
                          const IconComponent = field.icon;
                          const isEnabled = formData.enabledFields[key];
                          
                          return (
                            <label 
                              key={key}
                              className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                isEnabled 
                                  ? 'border-emerald-200 bg-emerald-50' 
                                  : 'border-gray-200 bg-white hover:border-gray-300'
                              }`}
                            >
                              <div className="flex-shrink-0">
                                <input
                                  type="checkbox"
                                  checked={isEnabled || false}
                                  onChange={(e) => handleInputChange('enabledFields', {
                                    ...formData.enabledFields,
                                    [key]: e.target.checked
                                  })}
                                  className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComponent className={`h-4 w-4 ${isEnabled ? 'text-emerald-600' : 'text-gray-400'}`} />
                                  <span className="font-medium text-gray-900">{field.label}</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {field.type === 'textarea' ? 'Multi-line text input' :
                                   field.type === 'select' ? 'Dropdown selection' :
                                   field.type === 'checkbox' ? 'Yes/no checkbox' :
                                   'Single line text input'}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Event Information</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure the basic details about your event that will be displayed to visitors.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Name *
                    </label>
                    <input
                      type="text"
                      value={formData.botName}
                      onChange={(e) => handleInputChange('botName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Amazing Summer Festival 2024"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.eventDate}
                      onChange={(e) => handleInputChange('eventDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={formData.duration}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="e.g. 3 hours, All day, 2 days"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Attendees
                    </label>
                    <input
                      type="number"
                      value={formData.maxAttendees}
                      onChange={(e) => handleInputChange('maxAttendees', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => handleInputChange('timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="Europe/London">London (GMT)</option>
                      <option value="Europe/Paris">Paris (CET)</option>
                      <option value="Asia/Tokyo">Tokyo (JST)</option>
                      <option value="Australia/Sydney">Sydney (AEST)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Describe your event, what attendees can expect, and why they should join..."
                  />
                </div>

                {/* Image Uploads */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Event Images</h4>
                  
                  {/* Hero Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Image
                    </label>
                    <FileUpload
                        onFileSelect={(file, error) => {
                        if (error) {
                            setUploadStates(prev => ({ ...prev, heroImage: { uploading: false, error } }));
                        } else {
                            handleFileUpload('heroImage', file);
                        }
                        }}
                        currentFile={formData.heroImage}
                        onRemove={() => setFormData(prev => ({ ...prev, heroImage: '' }))}
                        accept="image/*"
                        maxSize={15}
                        label="Upload Hero Image"
                        uploading={uploadStates.heroImage?.uploading}
                        error={uploadStates.heroImage?.error}
                        showPreview={true}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Recommended: 1920x1080px for best quality
                    </p>
                    </div>

                    {/* Logo */}
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Logo
                    </label>
                    <FileUpload
                        onFileSelect={(file, error) => {
                        if (error) {
                            setUploadStates(prev => ({ ...prev, logo: { uploading: false, error } }));
                        } else {
                            handleFileUpload('logo', file);
                        }
                        }}
                        currentFile={formData.logoUrl}
                        onRemove={() => setFormData(prev => ({ ...prev, logoUrl: '' }))}
                        accept="image/*"
                        maxSize={5}
                        label="Upload Logo"
                        uploading={uploadStates.logo?.uploading}
                        error={uploadStates.logo?.error}
                        showPreview={true}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Square format recommended (500x500px)
                    </p>
                    </div>
                </div>
                </div>
            )}

            {activeTab === 'header' && (
              <HeaderSettings
                formData={formData}
                onInputChange={handleInputChange}              
                onImageUpload={handleFileUpload}
              />
            )}

            {/* Location & Logistics Tab */}
            {activeTab === 'location' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Location & Logistics</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure location details and logistical information for your event.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Location *
                    </label>
                    <input
                      type="text"
                      value={formData.eventLocation}
                      onChange={(e) => handleInputChange('eventLocation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="City, venue name, or general area"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Venue Name
                    </label>
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => handleInputChange('venue', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Convention Center, Hotel Name, etc."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Address
                    </label>
                    <input
                      type="text"
                      value={formData.fullAddress}
                      onChange={(e) => handleInputChange('fullAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="123 Main St, City, State, ZIP"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Google Maps Link
                    </label>
                    <input
                      type="url"
                      value={formData.mapLink}
                      onChange={(e) => handleInputChange('mapLink', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="https://maps.google.com/..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weather Backup Plan
                    </label>
                    <input
                      type="text"
                      value={formData.weatherBackup}
                      onChange={(e) => handleInputChange('weatherBackup', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Indoor alternative location"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Venue Details & Directions
                  </label>
                  <textarea
                    value={formData.venueDetails}
                    onChange={(e) => handleInputChange('venueDetails', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Building entrance instructions, landmark references, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parking Information
                    </label>
                    <textarea
                      value={formData.parkingInfo}
                      onChange={(e) => handleInputChange('parkingInfo', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Parking availability, costs, restrictions..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Public Transportation
                    </label>
                    <textarea
                      value={formData.publicTransport}
                      onChange={(e) => handleInputChange('publicTransport', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Nearest subway, bus routes, train stations..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accessibility Information
                  </label>
                  <textarea
                    value={formData.accessibilityInfo}
                    onChange={(e) => handleInputChange('accessibilityInfo', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Wheelchair accessibility, assistance available, etc."
                  />
                </div>
              </div>
            )}

            {/* Booking Settings Tab */}
            {activeTab === 'booking' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Booking Configuration</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure how attendees can register and book for your event.
                  </p>
                </div>

                {/* Enable Booking */}
                <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div>
                    <h4 className="font-medium text-emerald-900">Enable Event Booking</h4>
                    <p className="text-sm text-emerald-700">Allow people to register for your event</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.requireBooking}
                    onChange={(e) => {
                      console.log('requireBooking toggled:', e.target.checked);
                      handleInputChange('requireBooking', e.target.checked);
                    }}
                    className="w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                </div>

                {formData.requireBooking && (
                  <div className="space-y-6">
                    {/* Booking Access */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Public Booking</h4>
                          <p className="text-sm text-gray-600">Anyone can book this event</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.publicBooking}
                          onChange={(e) => handleInputChange('publicBooking', e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Require Login to Book</h4>
                          <p className="text-sm text-gray-600">Users must sign in to make bookings</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.requireLoginForBooking}
                          onChange={(e) => handleInputChange('requireLoginForBooking', e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Auto-Confirm Bookings</h4>
                          <p className="text-sm text-gray-600">Automatically approve new bookings</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.autoConfirm}
                          onChange={(e) => handleInputChange('autoConfirm', e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                      </div>

                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Allow Waitlist</h4>
                          <p className="text-sm text-gray-600">When event is full, allow waitlist signups</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.allowWaitlist}
                          onChange={(e) => handleInputChange('allowWaitlist', e.target.checked)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                      </div>
                    </div>

                    {/* Payment Settings */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-medium text-yellow-900">Require Payment</h4>
                          <p className="text-sm text-yellow-700">Charge attendees for this event</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={formData.requirePayment}
                          onChange={(e) => handleInputChange('requirePayment', e.target.checked)}
                          className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                        />
                      </div>

                      {formData.requirePayment && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Event Price ($)
                            </label>
                            <input
                              type="number"
                              value={formData.eventPrice}
                              onChange={(e) => handleInputChange('eventPrice', parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                              min="0"
                              step="0.01"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Booking Deadline
                            </label>
                            <input
                              type="datetime-local"
                              value={formData.bookingDeadline}
                              onChange={(e) => handleInputChange('bookingDeadline', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Email Settings */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Email Notifications</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h5 className="font-medium text-gray-900">Confirmation Emails</h5>
                            <p className="text-sm text-gray-600">Send booking confirmation emails</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.confirmationEmail}
                            onChange={(e) => handleInputChange('confirmationEmail', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h5 className="font-medium text-gray-900">Reminder Emails</h5>
                            <p className="text-sm text-gray-600">Send event reminder emails</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.reminderEmails}
                            onChange={(e) => handleInputChange('reminderEmails', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Event Features</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable additional features for your event management and attendee experience.
                  </p>
                </div>

                {/* Core Features */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Core Features</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      {
                        key: 'giftRegistryEnabled',
                        title: 'Gift Registry',
                        desc: 'Allow guests to view and purchase gifts',
                        icon: Gift,
                        publicKey: 'giftRegistryPublic',
                        publicLabel: 'Make gift registry public'
                      },
                      {
                        key: 'milestoneTracking',
                        title: 'Milestone Tracking',
                        desc: 'Track event planning progress',
                        icon: CheckCircle,
                        publicKey: 'publicMilestones',
                        publicLabel: 'Show milestones to attendees'
                      },
                      {
                        key: 'galleryManagement',
                        title: 'Event Galleries',
                        desc: 'Photo and video galleries',
                        icon: Camera,
                        publicKey: 'publicGallery',
                        publicLabel: 'Make galleries public'
                      },
                      {
                        key: 'checkInSystem',
                        title: 'Check-in System',
                        desc: 'Track attendee check-ins on event day',
                        icon: Users
                      },
                      {
                        key: 'supplierManagement',
                        title: 'Supplier Management',
                        desc: 'Manage vendors and suppliers',
                        icon: Truck
                      },
                      {
                        key: 'enableQRCode',
                        title: 'QR Code Integration',
                        desc: 'Generate QR codes for easy check-in',
                        icon: Users
                      }
                    ].map(feature => {
                      const IconComponent = feature.icon;
                      return (
                        <div key={feature.key} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <IconComponent className="h-5 w-5 text-emerald-600" />
                              <h5 className="font-medium text-gray-900">{feature.title}</h5>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData[feature.key]}
                              onChange={(e) => handleInputChange(feature.key, e.target.checked)}
                              className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                            />
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{feature.desc}</p>
                          
                          {feature.publicKey && formData[feature.key] && (
                            <div className="flex items-center justify-between pt-2 border-t">
                              <span className="text-sm text-gray-700">{feature.publicLabel}</span>
                              <input
                                type="checkbox"
                                checked={formData[feature.publicKey]}
                                onChange={(e) => handleInputChange(feature.publicKey, e.target.checked)}
                                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Interaction Features */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Interaction & Engagement</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Allow Comments</h5>
                        <p className="text-sm text-gray-600">Let attendees leave comments on the event</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.allowComments}
                        onChange={(e) => handleInputChange('allowComments', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Allow Sharing</h5>
                        <p className="text-sm text-gray-600">Enable social media sharing buttons</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.allowSharing}
                        onChange={(e) => handleInputChange('allowSharing', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Show Attendee Count</h5>
                        <p className="text-sm text-gray-600">Display number of confirmed attendees</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.showAttendeeCount}
                        onChange={(e) => handleInputChange('showAttendeeCount', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Moderate Comments</h5>
                        <p className="text-sm text-gray-600">Review comments before they appear</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.moderateComments}
                        onChange={(e) => handleInputChange('moderateComments', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'design' && (
                          <DesignBrandingSection
                            formData={formData}
                            onInputChange={handleInputChange}
                            templateType="institution"
                          />
                        )}   

            {/* Styling & Branding Tab 
            {activeTab === 'styling' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Styling & Branding</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Customize the visual appearance and branding of your event page.
                  </p>
                </div>

                {/* Color Scheme 
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Color Scheme</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { key: 'primaryColor', label: 'Primary Color', desc: 'Main theme color' },
                      { key: 'accentColor', label: 'Accent Color', desc: 'Buttons and highlights' },
                      { key: 'backgroundColor', label: 'Background Color', desc: 'Page background' },
                      { key: 'headerColor', label: 'Header Background', desc: 'Header section background' },
                      { key: 'headerTextColor', label: 'Header Text Color', desc: 'Text color in header' }
                    ].map(color => (
                      <div key={color.key} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {color.label}
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="color"
                            value={formData[color.key]}
                            onChange={(e) => handleInputChange(color.key, e.target.value)}
                            className="w-12 h-10 rounded border border-gray-300"
                          />
                          <input
                            type="text"
                            value={formData[color.key]}
                            onChange={(e) => handleInputChange(color.key, e.target.value)}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            placeholder="#000000"
                          />
                        </div>
                        <p className="text-xs text-gray-500">{color.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Header Background Opacity
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData.styling?.headerOpacity ?? 100}
                        onChange={(e) => handleInputChange('styling.headerOpacity', parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                      />
                      <div className="w-16 px-3 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-300 rounded-lg text-center font-medium">
                        {formData.styling?.headerOpacity ?? 100}%
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 px-1">
                      <span>Transparent</span>
                      <span>Opaque</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Adjust the transparency of the header background
                    </p>
                  </div>
                </div>

                {/* Typography 
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Typography</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Font Family
                    </label>
                    <select
                      value={formData.fontFamily}
                      onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    >
                      <option value="Inter, sans-serif">Inter (Modern Sans)</option>
                      <option value="'Roboto', sans-serif">Roboto (Clean Sans)</option>
                      <option value="'Open Sans', sans-serif">Open Sans (Friendly Sans)</option>
                      <option value="'Lato', sans-serif">Lato (Corporate Sans)</option>
                      <option value="'Poppins', sans-serif">Poppins (Rounded Sans)</option>
                      <option value="'Playfair Display', serif">Playfair Display (Elegant Serif)</option>
                      <option value="'Merriweather', serif">Merriweather (Readable Serif)</option>
                      <option value="'Dancing Script', cursive">Dancing Script (Script)</option>
                    </select>
                  </div>
                </div>

                {/* Layout & Effects 
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Layout & Effects</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Header Border Radius
                      </label>
                      <select
                        value={formData.headerRadius}
                        onChange={(e) => handleInputChange('headerRadius', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="0px">None (0px)</option>
                        <option value="8px">Small (8px)</option>
                        <option value="16px">Medium (16px)</option>
                        <option value="24px">Large (24px)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Card Border Radius
                      </label>
                      <select
                        value={formData.cardRadius}
                        onChange={(e) => handleInputChange('cardRadius', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="0px">None (0px)</option>
                        <option value="8px">Small (8px)</option>
                        <option value="12px">Medium (12px)</option>
                        <option value="16px">Large (16px)</option>
                        <option value="24px">Extra Large (24px)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Shadow Level
                      </label>
                      <select
                        value={formData.shadowLevel}
                        onChange={(e) => handleInputChange('shadowLevel', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="none">None</option>
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                        <option value="extra-large">Extra Large</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Preview 
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Live Preview</h4>
                  <div 
                    className="w-full h-40 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center"
                    style={{
                      backgroundColor: formData.backgroundColor,
                      borderRadius: formData.cardRadius,
                      fontFamily: formData.fontFamily,
                      boxShadow: `0 4px 6px -1px rgba(0, 0, 0, ${formData.shadowLevel === 'none' ? '0' : '0.1'})`
                    }}
                  >
                    <div 
                      className="px-6 py-3 rounded text-white font-medium"
                      style={{ backgroundColor: formData.primaryColor }}
                    >
                      Book Now - Sample Button
                    </div>
                  </div>
                </div>
              </div>
            )}

            */}

            {/* Contact/Organizer Info Tab */}
            {activeTab === 'organizers' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Event Organizers</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Invite team members to help manage this event
                    </p>
                  </div>

                  {/* Invitation Component */}
                  <OrganizerInvitation 
                    bookData={book} 
                    bookId={book.id}
                    onInvitationSent={() => {
                      // Optionally refresh data
                    }}
                  />

                  {/* Current Organizers List */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Current Organizers</h4>
                    <div className="space-y-3">
                      {formData.organizers.map((organizer) => (
                        <div 
                          key={organizer.id} 
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                              {organizer.avatar ? (
                                <img src={organizer.avatar} alt={organizer.name} className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{organizer.name}</p>
                              <p className="text-sm text-gray-600">{organizer.email}</p>
                              {organizer.isMain && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800 mt-1">
                                  Primary Organizer
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {!organizer.isMain && (
                            <button
                              type="button"
                              onClick={async () => {
                                if (window.confirm(`Remove ${organizer.name} as organizer?`)) {
                                  try {
                                    await invitationsService.removeOrganizer(book.id, organizer.userId);
                                    // Refresh book data
                                    window.location.reload();
                                  } catch (error) {
                                    alert('Failed to remove organizer');
                                  }
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-700 font-medium"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            
            {activeTab === 'messages' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Messages & Updates</h3>
                    <p className="text-gray-600 mb-6">Configure messaging and updates functionality for your event.</p>
                  </div>

                  {/* Messages Settings */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                          <MessageSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Event Messages</h4>
                          <p className="text-sm text-gray-600">Allow attendees to send messages</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="enableMessages"
                          checked={formData.enableMessages}
                          onChange={(e) => handleInputChange('enableMessages', e.target.checked)}
                          className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <label htmlFor="enableMessages" className="ml-3 text-sm font-medium text-gray-700">
                          Enable Messages
                        </label>
                      </div>
                    </div>

                    {formData.enableMessages && (
                      <div className="space-y-4 pl-13 border-l-2 border-gray-100 ml-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">Moderate Messages</h5>
                            <p className="text-sm text-gray-600">Review messages before they appear publicly</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.moderateMessages}
                            onChange={(e) => handleInputChange('moderateMessages', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">Anonymous Messages</h5>
                            <p className="text-sm text-gray-600">Allow messages without requiring login</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.allowAnonymousMessages}
                            onChange={(e) => handleInputChange('allowAnonymousMessages', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Who can send messages?
                          </label>
                          <div className="space-y-2">
                            {[
                              { value: 'anyone', label: 'Anyone' },
                              { value: 'booked_attendee', label: 'Booked Attendees Only' },
                              { value: 'organizer', label: 'Organizers Only' }
                            ].map(option => (
                              <label key={option.value} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.messagingRoles.includes(option.value)}
                                  onChange={(e) => {
                                    const roles = e.target.checked
                                      ? [...formData.messagingRoles, option.value]
                                      : formData.messagingRoles.filter(role => role !== option.value);
                                    handleInputChange('messagingRoles', roles);
                                  }}
                                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Updates Settings */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                          <Bell className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Event Updates</h4>
                          <p className="text-sm text-gray-600">Share updates and announcements</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="enableUpdates"
                          checked={formData.enableUpdates}
                          onChange={(e) => handleInputChange('enableUpdates', e.target.checked)}
                          className="w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <label htmlFor="enableUpdates" className="ml-3 text-sm font-medium text-gray-700">
                          Enable Updates
                        </label>
                      </div>
                    </div>

                    {formData.enableUpdates && (
                      <div className="space-y-4 pl-13 border-l-2 border-gray-100 ml-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900">Moderate Updates</h5>
                            <p className="text-sm text-gray-600">Review updates before publishing</p>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.moderateUpdates}
                            onChange={(e) => handleInputChange('moderateUpdates', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Who can post updates?
                          </label>
                          <div className="space-y-2">
                            {[
                              { value: 'organizer', label: 'Organizers Only' },
                              { value: 'booked_attendee', label: 'Booked Attendees' },
                              { value: 'anyone', label: 'Anyone' }
                            ].map(option => (
                              <label key={option.value} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={formData.updatesRoles.includes(option.value)}
                                  onChange={(e) => {
                                    const roles = e.target.checked
                                      ? [...formData.updatesRoles, option.value]
                                      : formData.updatesRoles.filter(role => role !== option.value);
                                    handleInputChange('updatesRoles', roles);
                                  }}
                                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <span className="ml-3 text-sm text-gray-700">{option.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Moderation Dashboard Preview */}
                  {(formData.moderateMessages || formData.moderateUpdates) && (
                    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Shield className="h-5 w-5 text-purple-600" />
                        <h4 className="font-semibold text-purple-900">Moderation Dashboard</h4>
                      </div>
                      <p className="text-purple-700 text-sm mb-4">
                        With moderation enabled, you'll have access to a dedicated dashboard to review and approve content before it goes live.
                      </p>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm text-purple-700">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          Pending Review
                        </div>
                        <div className="flex items-center gap-2 text-sm text-purple-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          Approved
                        </div>
                        <div className="flex items-center gap-2 text-sm text-purple-700">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          Rejected
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {/* Access & Privacy Tab */}
            {activeTab === 'access' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Access & Privacy Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Control who can view your event and what information is publicly visible.
                  </p>
                </div>

                {/* Access Level */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Event Visibility</h4>
                  
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="accessLevel"
                        value="public"
                        checked={formData.accessLevel === 'public'}
                        onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                        className="mt-1 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Globe className="h-4 w-4 text-emerald-600" />
                          <span className="font-medium text-gray-900">Public</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Anyone can view your event page and information
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="accessLevel"
                        value="private"
                        checked={formData.accessLevel === 'private'}
                        onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                        className="mt-1 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="h-4 w-4 text-gray-600" />
                          <span className="font-medium text-gray-900">Private</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Only people with the direct link can view your event
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="accessLevel"
                        value="password"
                        checked={formData.accessLevel === 'password'}
                        onChange={(e) => handleInputChange('accessLevel', e.target.value)}
                        className="mt-1 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="h-4 w-4 text-red-600" />
                          <span className="font-medium text-gray-900">Password Protected</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          Require a password to view the event page
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Additional Privacy Settings */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Privacy Controls</h4>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Allow Anonymous Viewing</h5>
                        <p className="text-sm text-gray-600">Visitors don't need to sign in to view the event</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.allowAnonymous}
                        onChange={(e) => handleInputChange('allowAnonymous', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h5 className="font-medium text-gray-900">Moderate Comments</h5>
                        <p className="text-sm text-gray-600">Review comments before they appear publicly</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={formData.moderateComments}
                        onChange={(e) => handleInputChange('moderateComments', e.target.checked)}
                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Publishing Status */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-blue-900">Publish Event</h4>
                      <p className="text-sm text-blue-700">
                        Make your event live and accessible to visitors
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.published}
                      onChange={(e) => handleInputChange('published', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* SEO & Domain Tab */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Domain & SEO Configuration</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure your custom domain and optimize your event for search engines and social media sharing.
                  </p>
                </div>

                {/* URL Configuration */}
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-6 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">URL Configuration</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Domain
                      </label>
                      <input
                        type="text"
                        value={formData.customDomain}
                        onChange={(e) => handleInputChange('customDomain', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="event.yourdomain.com"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Your own custom domain for this event
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Path
                      </label>
                      <input
                        type="text"
                        value={formData.customPath}
                        onChange={(e) => handleInputChange('customPath', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="summer-festival-2024"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        URL path: parrotpages.com/your-path
                      </p>
                    </div>
                  </div>

                  {/* URL Preview */}
                  {(formData.customDomain || formData.customPath) && (
                    <div className="mt-4 p-3 bg-white rounded border">
                      <p className="text-sm text-gray-600 mb-1">Your event will be accessible at:</p>
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {formData.customDomain ? 
                            `https://${formData.customDomain}` : 
                            `https://parrotpages.com/${formData.customPath || 'your-event'}`
                          }
                        </div>
                        <ExternalLink className="h-3 w-3 text-gray-400" />
                      </div>
                    </div>
                  )}
                </div>

                {/* SEO Content */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">SEO Content</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Title
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Title for search engines (50-60 characters)"
                      maxLength="60"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.seoTitle.length}/60 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Description
                    </label>
                    <textarea
                      value={formData.seoDescription}
                      onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="Description for search engines and social media (150-160 characters)"
                      maxLength="160"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.seoDescription.length}/160 characters
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SEO Keywords
                    </label>
                    <input
                      type="text"
                      value={formData.seoKeywords}
                      onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="event, conference, summer, festival, networking"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Separate keywords with commas
                    </p>
                  </div>
                </div>

                {/* SEO Images */}
                <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">SEO Images</h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Favicon */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                        Favicon
                        </label>
                        <FileUpload
                        onFileSelect={(file, error) => {
                            if (error) {
                            setUploadStates(prev => ({ ...prev, favicon: { uploading: false, error } }));
                            } else {
                            handleFileUpload('favicon', file);
                            }
                        }}
                        currentFile={formData.favicon}
                        onRemove={() => setFormData(prev => ({ ...prev, favicon: '' }))}
                        accept="image/*"
                        maxSize={1}
                        label="Upload Favicon"
                        uploading={uploadStates.favicon?.uploading}
                        error={uploadStates.favicon?.error}
                        showPreview={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                        32x32px ICO or PNG recommended
                        </p>
                    </div>

                    {/* Open Graph Image */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                        Social Media Image (OG Image)
                        </label>
                        <FileUpload
                        onFileSelect={(file, error) => {
                            if (error) {
                            setUploadStates(prev => ({ ...prev, ogImage: { uploading: false, error } }));
                            } else {
                            handleFileUpload('ogImage', file);
                            }
                        }}
                        currentFile={formData.ogImage}
                        onRemove={() => setFormData(prev => ({ ...prev, ogImage: '' }))}
                        accept="image/*"
                        maxSize={5}
                        label="Upload OG Image"
                        uploading={uploadStates.ogImage?.uploading}
                        error={uploadStates.ogImage?.error}
                        showPreview={true}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                        1200x630px recommended for social sharing
                        </p>
                    </div>
                    </div>
                </div>
                </div>
            )}


            {/* Advanced Tab */}
            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure advanced features and integrations for your event.
                  </p>
                </div>

                {/* Analytics & Tracking */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Analytics & Tracking</h4>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h5 className="font-medium text-gray-900">Enable Analytics</h5>
                      <p className="text-sm text-gray-600">Track visitor behavior and event performance</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.enableAnalytics}
                      onChange={(e) => handleInputChange('enableAnalytics', e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                </div>

                {/* Localization */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Localization</h4>
                  
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <h5 className="font-medium text-gray-900">Multi-Language Support</h5>
                      <p className="text-sm text-gray-600">Enable multiple language versions of your event</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.enableMultiLanguage}
                      onChange={(e) => handleInputChange('enableMultiLanguage', e.target.checked)}
                      className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                  </div>
                </div>

                {/* Custom CSS */}
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Custom Styling</h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom CSS
                    </label>
                    <textarea
                      value={formData.customCSS}
                      onChange={(e) => handleInputChange('customCSS', e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                      placeholder="/* Add your custom CSS here */
.event-header {
  background: linear-gradient(45deg, #your-colors);
}

.booking-button {
  border-radius: 25px;
}"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Add custom CSS to further customize your event's appearance
                    </p>
                  </div>
                </div>

                {/* Developer Settings */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-3">Developer Settings</h4>
                  <p className="text-sm text-yellow-700 mb-4">
                    These settings are for advanced users and developers. Use with caution.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom JavaScript (Advanced)
                      </label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                        placeholder="// Custom JavaScript support coming soon"
                        disabled
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Custom JavaScript support coming soon
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preview Tab */}
            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Event Preview</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Preview how your event will look on different devices before publishing.
                  </p>
                </div>

                {/* Device Toggle */}
                <div className="flex justify-center mb-6">
                  <div className="bg-gray-100 rounded-lg p-1 inline-flex">
                    {['desktop', 'tablet', 'mobile'].map(device => (
                      <button
                        key={device}
                        onClick={() => setPreviewMode(device)}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          previewMode === device
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {device.charAt(0).toUpperCase() + device.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Preview Frame */}
                <div className="flex justify-center">
                  <div 
                    className={`border-2 border-gray-300 rounded-lg overflow-hidden bg-white shadow-lg ${
                      previewMode === 'mobile' ? 'w-80 h-[600px]' :
                      previewMode === 'tablet' ? 'w-[768px] h-[500px]' :
                      'w-full max-w-6xl h-[600px]'
                    }`}
                    style={{
                      backgroundColor: formData.backgroundColor,
                      fontFamily: formData.fontFamily
                    }}
                  >
                    {/* Mock Event Preview */}
                    <div 
                      className="relative h-48 flex items-center justify-center text-white"
                      style={{
                        backgroundColor: formData.primaryColor,
                        backgroundImage: formData.heroImage ? `url(${formData.heroImage})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                      <div className="relative text-center">
                        <h1 className="text-2xl md:text-4xl font-bold mb-2">
                          {formData.botName || 'Your Event Name'}
                        </h1>
                        <p className="text-lg opacity-90">
                          {formData.eventDate ? new Date(formData.eventDate).toLocaleDateString() : 'Event Date'} • {formData.eventLocation || 'Event Location'}
                        </p>
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="max-w-4xl mx-auto">
                        {/* Event Description */}
                        <div className="mb-6">
                          <p className="text-gray-700 leading-relaxed">
                            {formData.description || 'Your event description will appear here. This preview shows how your event will look to visitors.'}
                          </p>
                        </div>

                        {/* Mock Booking Button */}
                        {formData.requireBooking && (
                          <div className="text-center mb-6">
                            <button
                              className="text-white font-semibold py-3 px-8 rounded-lg shadow-md"
                              style={{ 
                                backgroundColor: formData.accentColor,
                                borderRadius: formData.cardRadius 
                              }}
                            >
                              {formData.requirePayment ? 
                                `Book Now - $${formData.eventPrice}` : 
                                'Reserve Your Spot - Free'
                              }
                            </button>
                          </div>
                        )}

                        {/* Mock Features */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {formData.giftRegistryEnabled && (
                            <div 
                              className="p-4 rounded-lg border"
                              style={{ borderRadius: formData.cardRadius }}
                            >
                              <Gift className="h-6 w-6 mb-2" style={{ color: formData.primaryColor }} />
                              <h4 className="font-medium">Gift Registry</h4>
                              <p className="text-sm text-gray-600">View gift suggestions</p>
                            </div>
                          )}

                          {formData.galleryManagement && (
                            <div 
                              className="p-4 rounded-lg border"
                              style={{ borderRadius: formData.cardRadius }}
                            >
                              <Camera className="h-6 w-6 mb-2" style={{ color: formData.primaryColor }} />
                              <h4 className="font-medium">Event Gallery</h4>
                              <p className="text-sm text-gray-600">View photos & videos</p>
                            </div>
                          )}

                          {formData.milestoneTracking && (
                            <div 
                              className="p-4 rounded-lg border"
                              style={{ borderRadius: formData.cardRadius }}
                            >
                              <CheckCircle className="h-6 w-6 mb-2" style={{ color: formData.primaryColor }} />
                              <h4 className="font-medium">Planning Progress</h4>
                              <p className="text-sm text-gray-600">Track milestones</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview URL */}
                {previewUrl && (
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Your event will be live at:</p>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {previewUrl.replace('https://', '')}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}
              </div>
            )}
            
          </div>
        </div>

        {/* Footer with Save Button */}
        <div className="flex justify-between items-center p-6 border-t bg-gray-50">
          <div className="flex items-center gap-4">
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                Preview: {previewUrl.replace('https://', '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Event Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};